import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { TranslatableInput } from "./TranslatableInput";
import { useLocalized } from "@/hooks/useLocalized";

import { LocalizedService, LocalizedContent } from "@/types";
import { Plus, Edit, Trash2, Upload, DollarSign, Clock, ArrowUp, ArrowDown, X, ArrowLeft, ArrowRight } from "lucide-react";
import { auditLog } from "@/lib/auditLog";


interface Category {
  id: string;
  name: LocalizedContent;
  description: LocalizedContent;
  is_active: boolean;
}


type Service = LocalizedService;

export function ServicesManager() {
  const { getLocalized } = useLocalized();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [uploadingServices, setUploadingServices] = useState<Set<string>>(new Set());
  const { formatPrice } = useCurrency();

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} hr`;
    return `${hours} hr ${mins} min`;
  };


  const [formData, setFormData] = useState<{
    name: LocalizedContent;
    category: string;
    description: LocalizedContent;
    duration_minutes: number;
    base_price: number;
    is_active: boolean;
  }>({
    name: '',
    category: '',
    description: '',
    duration_minutes: 60,
    base_price: 0,
    is_active: true
  });

  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: "Error",
        description: "Failed to fetch services",
        variant: "destructive",
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, [fetchServices, fetchCategories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingService) {
        const { error } = await supabase
          .from('services')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update(formData as any)
          .eq('id', editingService.id);

        if (error) throw error;
        toast({ title: "Service updated successfully!" });
        auditLog.service.update(editingService.id, formData.name);
      } else {
        const { data: profile } = await supabase.from('photographer_profiles').select('id').single();

        // Get max sort order
        const { data: maxOrder } = await supabase.from('services').select('sort_order').order('sort_order', { ascending: false }).limit(1);
        const nextOrder = (maxOrder?.[0]?.sort_order || 0) + 1;

        const { error } = await supabase
          .from('services')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert([{ ...formData, photographer_id: profile?.id, sort_order: nextOrder }] as any);

        if (error) throw error;
        toast({ title: "Service created successfully!" });
        auditLog.service.create("new", formData.name); // We don't have ID here easily without returning it, passing "new" or getting ID. For now "new" is fine or omitting ID.
      }

      fetchServices();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: "Error",
        description: "Failed to save service",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this service?')) return;
    try {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
      toast({ title: "Service deleted successfully!" });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({ title: "Error", description: "Failed to delete service", variant: "destructive" });
    }
  };

  const moveService = async (id: string, direction: 'up' | 'down') => {
    const index = services.findIndex(s => s.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === services.length - 1) return;

    const otherIndex = direction === 'up' ? index - 1 : index + 1;
    const otherService = services[otherIndex];
    const currentService = services[index];

    try {
      await supabase.from('services').update({ sort_order: otherService.sort_order }).eq('id', currentService.id);
      await supabase.from('services').update({ sort_order: currentService.sort_order }).eq('id', otherService.id);
      fetchServices();
    } catch (error) {
      console.error('Error moving service:', error);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, serviceId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingServices(prev => new Set(prev).add(serviceId));
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${serviceId}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('services').upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('services').getPublicUrl(fileName);

      const service = services.find(s => s.id === serviceId);
      const currentUrls = service?.media_urls || [];
      const newUrls = [...currentUrls, publicUrl];

      const { error } = await supabase.from('services').update({ media_urls: newUrls } as any).eq('id', serviceId);
      if (error) throw error;

      toast({ title: "Image uploaded successfully!" });
      fetchServices();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setUploadingServices(prev => {
        const next = new Set(prev);
        next.delete(serviceId);
        return next;
      });
    }
  };

  const handleDeleteImage = async (serviceId: string, urlToDelete: string) => {
    if (!confirm('Delete this image?')) return;
    try {
      const service = services.find(s => s.id === serviceId);
      const newUrls = service?.media_urls.filter(url => url !== urlToDelete) || [];
      await supabase.from('services').update({ media_urls: newUrls } as any).eq('id', serviceId);
      fetchServices();
      toast({ title: "Image deleted" });
    } catch (err) {
      console.error(err);
    }
  };

  const handleMoveImage = async (serviceId: string, url: string, direction: 'left' | 'right') => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return;
    const urls = [...(service.media_urls || [])];
    const index = urls.indexOf(url);
    if (index === -1) return;

    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= urls.length) return;

    [urls[index], urls[newIndex]] = [urls[newIndex], urls[index]];

    try {
      await supabase.from('services').update({ media_urls: urls } as any).eq('id', serviceId);
      fetchServices();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      category: service.category || '',
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      base_price: service.base_price,
      is_active: service.is_active
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      duration_minutes: 60,
      base_price: 0,
      is_active: true
    });
    setEditingService(null);
  };

  // Helper to get English name safely for backward compatibility used in category value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getEnglishName = (content: LocalizedContent): string => {
    if (typeof content === 'string') return content;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (content as any).en || Object.values(content)[0] || '';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Services</h2>
          <p className="text-muted-foreground">Manage your photography services and packages.</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Service
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        {/* ... trigger ... */}
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Edit Service' : 'Add New Service'}
            </DialogTitle>
            <DialogDescription>
              Create or modify your photography services
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <TranslatableInput
                  label="Service Name"
                  value={formData.name}
                  onChange={(val) => setFormData(prev => ({ ...prev, name: val }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value: string) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={getEnglishName(cat.name)}>
                        {getLocalized(cat.name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <TranslatableInput
                label="Description"
                component="textarea"
                value={formData.description}
                onChange={(val) => setFormData(prev => ({ ...prev, description: val }))}
              />
            </div>

            {/* ... duration, price ... */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Base Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) }))}
                  required
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="is_active">Active Status</Label>
              {/* Re-using switch logic, or simple checkbox for form if needed, but form uses is_active state */}
              <Button
                type="button"
                variant={formData.is_active ? "default" : "secondary"}
                onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
              >
                {formData.is_active ? "Active" : "Inactive"}
              </Button>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingService ? 'Update Service' : 'Create Service'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <Card key={service.id} className={`relative border-l-4 ${service.is_active ? 'border-l-primary' : 'border-l-muted'}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{getLocalized(service.name)}</CardTitle>
                  <Badge variant={service.is_active ? "default" : "secondary"} className="mt-1">
                    {service.category}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  {/* Move buttons */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => moveService(service.id, 'up')}
                    disabled={services.indexOf(service) === 0}
                    title="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => moveService(service.id, 'down')}
                    disabled={services.indexOf(service) === services.length - 1}
                    title="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(service)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  {hasPermission('delete_services') && (
                    <Button size="sm" variant="outline" onClick={() => handleDelete(service.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {service.media_urls && service.media_urls.length > 0 && (
                <div className="mb-3">
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                    {service.media_urls.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={url}
                          alt={`${getLocalized(service.name)} ${idx + 1}`}
                          className="w-full h-20 object-cover rounded"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-6 w-6"
                            disabled={idx === 0}
                            onClick={() => handleMoveImage(service.id, url, 'left')}
                          >
                            <ArrowLeft className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleDeleteImage(service.id, url)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-6 w-6"
                            disabled={idx === service.media_urls.length - 1}
                            onClick={() => handleMoveImage(service.id, url, 'right')}
                          >
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <CardDescription className="mb-3 line-clamp-2">
                {getLocalized(service.description)}
              </CardDescription>

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {formatDuration(service.duration_minutes)}
                </div>
                <div className="flex items-center gap-1 font-semibold text-primary">
                  <DollarSign className="h-4 w-4" />
                  {formatPrice(service.base_price)}
                </div>
              </div>

              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant={service.is_active ? "ghost" : "outline"}
                  className={`flex-1 ${service.is_active ? "text-red-500 hover:text-red-600 hover:bg-red-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}`}
                  onClick={async () => {
                    const { error } = await supabase.from('services').update({ is_active: !service.is_active } as any).eq('id', service.id);
                    if (!error) {
                      fetchServices();
                      toast({ title: `Service ${!service.is_active ? 'Activated' : 'Deactivated'}` });
                    }
                  }}
                >
                  {service.is_active ? "Deactivate" : "Activate"}
                </Button>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  disabled={uploadingServices.has(service.id)}
                  onClick={() => document.getElementById(`file-${service.id}`)?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {uploadingServices.has(service.id) ? 'Uploading...' : 'Add Image'}
                </Button>
                <input
                  id={`file-${service.id}`}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, service.id)}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {services.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground mb-4">No services found</p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Service
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}